import { edgeAccessOptionsResponseSchema } from "@/generated/edge-approval-contract";
/**
 * EdgeDeployments — queries + mutation runners.
 *
 * Tenant-scoped. Feature code targets the canonical platform path shape and the api-client rewrites
 * it to /api/tenant/v1/edge-deployments on a tenant host, which is where the backend mounts this surface.
 *
 * Inventory reads do not require step-up. Approval document reads are part of the interactive
 * ceremony and use withStepUp because the backend mounts them with approval management. Other reads
 * are mounted behind `edge.read`, which is not in the step-up set; only the writes sit behind
 * `edge.manage`, which is. Under the old single-permission model every GET on this surface could
 * raise a passkey ceremony, which forced three local workarounds that are all gone now: a promise
 * queue serializing the reads (the step-up store rejects a second concurrent challenge, and the
 * roster fans out one key read per edge), a ban on polling and focus refetch (each one could
 * prompt for a passkey while the operator was reading), and `retry: false` (a retry after a
 * cancelled ceremony would re-open the dialog). The reads now take the app-wide query defaults,
 * which already do the right thing — no retry on 4xx, two retries on 5xx, refetch on focus.
 *
 * If a read ever moves back behind a step-up permission, the wrapper has to come back WITH the queue
 * and the polling ban; half of that combination is a surface that fails on its second concurrent
 * read for a reason nobody can see.
 */

import { useReducer } from "react";
import { base64urlDecode, ceremonyReducer, runAssertion } from "@pureid/auth-core";
import { edgeApprovalBeginSchema, edgeApprovalSchema, edgeImageUpdateApprovalSchema, edgeCombinedApprovalSchema } from "@/generated/edge-approval-contract";
import { edgeRetirementResponseSchema } from "@/generated/edge-retirement-contract";
import type { EdgeManifestApproval } from "./schemas";
import { admitsPeople } from "./use-cases";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  edgeRegistrationSchema, edgeRegistrationsSchema, edgeRegistrationProposalSchema,
  edgeSiteSchema, edgeSitesSchema, type EdgeSite, type CreateEdgeSiteForm,
  type EdgeRegistration, type EdgeRegistrationProposal, type EdgeRegistrationDecision, type PrepareEdgeForm,
  attestationStateSchema,
  authoriseEdgeResponseSchema,
  splitPEMBlocks,
  containmentHistoryResponseSchema,
  edgeContainmentReceiptSchema,
  servingCertificateListSchema,
  servingCertificateRevocationSchema,
  type EdgeContainmentReceipt,
  type ServingCertificateList,
  type ServingCertificateRevocation,
  type RevocationReason,
  edgeCapabilitiesResponseSchema,
  evidenceKeysResponseSchema,
  edgeDeploymentsResponseSchema,
  edgeDecommissionSchema,
  edgeDecommissionsResponseSchema,
  grantMoveResponseSchema,
  relayAdmissionPolicySchema,
  type RelayAdmissionMode,
  type RelayAdmissionPolicy,
  type AttestationState,
  type AuthoriseEdgeForm,
  type AuthoriseEdgeResponse,
  type PinAttestationForm,
  type ContainmentEvent,
  type EvidenceKey,
  type EdgeCapabilitiesResponse,
  type EdgeAccessOption,
  type EdgeDeployment,
  type EdgeDecommission,
  type EdgeRetirement,
  type GrantMoveResponse,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/edge-deployments`;
}

/**
 * Shared read options: how live this surface is.
 *
 * 60s, matching the devices and audit surfaces, and it applies to the roster and the instance keys
 * rather than static configuration reads. During an incident the operator is watching two moving facts — is this
 * edge still enforcing, and is its instances' evidence still landing — and a console that froze
 * the moment it mounted would answer both with whatever was true when the page opened.
 */
const LIVE_READ_OPTIONS = {
  refetchInterval: 60_000,
} as const;

// ── Reads ─────────────────────────────────────────────────────────────────

export async function fetchEdgeDeployments(tenantID: string): Promise<EdgeDeployment[]> {
  const raw = await apiFetch<unknown>(base(tenantID));
  return edgeDeploymentsResponseSchema.parse(raw).edges;
}

export async function fetchEvidenceKeys(
  tenantID: string,
  edgeID: string,
): Promise<EvidenceKey[]> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/${encodeURIComponent(edgeID)}/evidence-keys`,
  );
  return evidenceKeysResponseSchema.parse(raw).evidence_keys;
}

/** The roster. Which door serves this tenant is derived from it, so nothing else may answer that. */
export function useEdgeDeploymentsQuery(tenantID: string) {
  return useQuery<EdgeDeployment[], ApiError>({
    queryKey: queryKeys.edgeDeployments.list(tenantID),
    queryFn: () => fetchEdgeDeployments(tenantID),
    enabled: tenantID.length > 0,
    refetchInterval: (query) => query.state.data?.some((edge) =>
      edge.installation?.state === "installing" || edge.installation?.connection === "connecting",
    ) ? 10_000 : LIVE_READ_OPTIONS.refetchInterval,
  });
}

/** The instance keys of ONE edge, for the drill-in panel. */
export function useEvidenceKeysQuery(tenantID: string, edgeID: string | undefined) {
  return useQuery<EvidenceKey[], ApiError>({
    queryKey: queryKeys.edgeDeployments.evidenceKeys(tenantID, edgeID ?? ""),
    queryFn: () => fetchEvidenceKeys(tenantID, edgeID ?? ""),
    enabled: tenantID.length > 0 && !!edgeID,
    ...LIVE_READ_OPTIONS,
  });
}

/**
 * Instance counts for the roster, one read per edge, and only once the roster has landed.
 *
 * The counts are the only tenant-visible answer to "how many processes are running under this
 * edge and is their evidence landing", because an instance IS an announced evidence key. A row
 * whose keys have not loaded reports nothing rather than reporting zero: zero instances and
 * unknown instances are different facts, and the second one must not render as the first.
 *
 * This fan-out runs in parallel. It could not before the edge.read split: every read raised a
 * step-up challenge, the store rejects a second concurrent one, and a promise queue had to serialize
 * them for that reason alone.
 */
export function useEvidenceKeySummaries(tenantID: string, edgeIDs: readonly string[]) {
  return useQueries({
    queries: edgeIDs.map((edgeID) => ({
      queryKey: queryKeys.edgeDeployments.evidenceKeys(tenantID, edgeID),
      queryFn: () => fetchEvidenceKeys(tenantID, edgeID),
      enabled: tenantID.length > 0,
      ...LIVE_READ_OPTIONS,
    })),
    combine: (results): Map<string, EvidenceKey[]> => {
      const byEdgeDeployment = new Map<string, EvidenceKey[]>();
      edgeIDs.forEach((edgeID, i) => {
        const data = results[i]?.data;
        if (results[i]?.isSuccess && data) byEdgeDeployment.set(edgeID, data);
      });
      return byEdgeDeployment;
    },
  });
}

// ── Writes ────────────────────────────────────────────────────────────────

/**
 * Activate or suspend. One mutation for both, because they are the same act in two directions and
 * splitting them would let the two copies of the invalidation drift.
 *
 * Activate closes the control plane's own door for this tenant from the next registry reload.
 * Suspend reopens it, and moves no traffic: the edge keeps answering whatever still routes to
 * it. The dialogs carry that in full.
 */
export function useEdgeDeploymentLifecycleMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<
    GrantMoveResponse | null,
    ApiError,
    { edgeID: string; act: "activate" | "suspend" }
  >({
    mutationFn: ({ edgeID, act }) =>
      withStepUp(async () => {
        const raw = await apiFetch<unknown>(
          `${base(tenantID)}/${encodeURIComponent(edgeID)}/${act}`,
          { method: "POST" },
        );
        // Lenient on purpose: the act has already taken effect, so a body this surface cannot read
        // must not present as a failure. The caller falls back to its own copy, which says the same
        // thing the server's does.
        const parsed = grantMoveResponseSchema.safeParse(raw);
        return parsed.success ? parsed.data : null;
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.edgeDeployments.list(tenantID) });
    },
  });
}

function decommissionsKey(tenantID: string, edgeID: string) {
  return [...queryKeys.edgeDeployments.all(tenantID), "decommissions", edgeID] as const;
}

function retirementKey(tenantID: string, edgeID: string) {
  return [...queryKeys.edgeDeployments.all(tenantID), "retirement", edgeID] as const;
}

export function useEdgeRetirementQuery(tenantID: string, edgeID: string | undefined) {
  return useQuery<EdgeRetirement | null, ApiError>({
    queryKey: retirementKey(tenantID, edgeID ?? ""),
    queryFn: async () => edgeRetirementResponseSchema.parse(await apiFetch<unknown>(
      `${base(tenantID)}/${encodeURIComponent(edgeID ?? "")}/retirement`,
    )).retirement,
    enabled: tenantID.length > 0 && !!edgeID,
    refetchInterval: (query) => query.state.data?.state === "pending" ? 10_000 : false,
  });
}

type RetirementAct =
  | { edgeID: string; act: "retire"; reason: string; confirmation: string }
  | { edgeID: string; act: "confirm-route"; reference: string };

export function useEdgeRetirementMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<EdgeRetirement | null, ApiError, RetirementAct>({
    mutationFn: (vars) => withStepUp(async () => {
      const raw = await apiFetch<unknown>(
        `${base(tenantID)}/${encodeURIComponent(vars.edgeID)}/retirement${vars.act === "confirm-route" ? "/confirm-route" : ""}`,
        { method: "POST", body: vars.act === "retire"
          ? { reason: vars.reason, confirmation: vars.confirmation }
          : { reference: vars.reference } },
      );
      return edgeRetirementResponseSchema.parse(raw).retirement;
    }),
    onSuccess: async (receipt, { edgeID }) => {
      const key = retirementKey(tenantID, edgeID);
      await qc.cancelQueries({ queryKey: key });
      qc.setQueryData(key, receipt);
    },
    // Reconcile even when a response is lost after the server commits retirement.
    onSettled: () => qc.invalidateQueries({ queryKey: queryKeys.edgeDeployments.all(tenantID) }),
  });
}

export function useEdgeDecommissionsQuery(tenantID: string, edgeID: string | undefined) {
  return useQuery<EdgeDecommission[], ApiError>({
    queryKey: decommissionsKey(tenantID, edgeID ?? ""),
    queryFn: async () => {
      const raw = await apiFetch<unknown>(
        `${base(tenantID)}/${encodeURIComponent(edgeID ?? "")}/decommissions`,
      );
      return edgeDecommissionsResponseSchema.parse(raw).decommissions;
    },
    enabled: tenantID.length > 0 && !!edgeID,
    refetchInterval: 10_000,
  });
}

type DecommissionAct =
  | { operationID: string; act: "approve" | "cancel"; reason: string }
  | { operationID: string; act: "confirm-customer-route"; reference: string };

export function useEdgeDecommissionMutation(tenantID: string, edgeID: string | undefined) {
  const qc = useQueryClient();
  return useMutation<EdgeDecommission | null, ApiError, DecommissionAct>({
    mutationFn: (vars) =>
      withStepUp(async () => {
        const raw = await apiFetch<unknown>(
          `${base(tenantID)}/${encodeURIComponent(edgeID ?? "")}/decommissions/${encodeURIComponent(vars.operationID)}/${vars.act}`,
          {
            method: "POST",
            body:
              vars.act === "confirm-customer-route"
                ? { reference: vars.reference }
                : { reason: vars.reason },
          },
        );
        const parsed = edgeDecommissionSchema.safeParse(raw);
        return parsed.success ? parsed.data : null;
      }),
    onSuccess: async (receipt) => {
      if (edgeID) {
        const key = decommissionsKey(tenantID, edgeID);
        await qc.cancelQueries({ queryKey: key });
        if (receipt) qc.setQueryData<EdgeDecommission[]>(key, (previous) =>
          [receipt, ...(previous ?? []).filter((operation) => operation.operation_id !== receipt.operation_id)]);
        void qc.invalidateQueries({ queryKey: key });
      }
      void qc.invalidateQueries({ queryKey: queryKeys.edgeDeployments.list(tenantID) });
    },
  });
}

/**
 * Confirm or withdraw an instance evidence key.
 *
 * The note is the operator's record of HOW the fingerprint was compared, and the out-of-band
 * comparison is the only thing that makes the act mean anything, so the form asks for it.
 */
export function useEvidenceKeyTrustMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<
    void,
    ApiError,
    { edgeID: string; thumbprint: string; trust: boolean; note?: string }
  >({
    mutationFn: ({ edgeID, thumbprint, trust, note }) =>
      withStepUp(async () => {
        const verb = trust ? "trust" : "untrust";
        await apiFetch<void>(
          `${base(tenantID)}/${encodeURIComponent(edgeID)}/evidence-keys/${encodeURIComponent(thumbprint)}/${verb}`,
          { method: "POST", body: trust ? { note: note ?? "" } : undefined },
        );
      }),
    onSuccess: (_data, { edgeID }) => {
      void qc.invalidateQueries({
        queryKey: queryKeys.edgeDeployments.evidenceKeys(tenantID, edgeID),
      });
    },
  });
}

/**
 * The break-glass confirmation: PureID confirming a key on an edge the CUSTOMER operates.
 *
 * A SEPARATE MUTATION BECAUSE IT IS A SEPARATE ROUTE AND A SEPARATE PERMISSION. The server refuses
 * the ordinary verb for a platform-scope actor on a customer-operated edge and names this one, so
 * without a caller the refusal was a dead end that only `curl` could get past — on the step that has
 * historically been the hardest to reach at all.
 */
export function useEvidenceKeyOverrideMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { edgeID: string; thumbprint: string; reason: string }>({
    mutationFn: ({ edgeID, thumbprint, reason }) =>
      withStepUp(async () => {
        await apiFetch<void>(
          `${base(tenantID)}/${encodeURIComponent(edgeID)}/evidence-keys/${encodeURIComponent(thumbprint)}/platform-override-trust`,
          { method: "POST", body: { reason } },
        );
      }),
    onSuccess: (_data, { edgeID }) => {
      void qc.invalidateQueries({
        queryKey: queryKeys.edgeDeployments.evidenceKeys(tenantID, edgeID),
      });
    },
  });
}

/**
 * Did the server refuse this because the caller is on the wrong side of the operator label?
 *
 * Read from the CODE, not the message: the sentence is written for a person and will be reworded,
 * while the code is the contract. `platform_override_required` is the one refusal that has a next
 * step rather than a cause.
 */
export function isOverrideRequired(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 409) return false;
  const body = err.body as { code?: unknown } | null;
  return typeof body?.code === "string" && body.code === "platform_override_required";
}

// ── What this edge may do with the tenant's authority ──────────────────────
//
// THE ADDITIVE PAIR, NEVER THE REPLACE. `PUT /{edge_id}/capabilities` exists and this file does not
// call it, deliberately: it takes the COMPLETE set and deletes whatever it does not name, so
// "also allow this" expressed against it withdraws the serve set in the same statement and the edge
// stops pushing evidence with the symptom arriving hours later somewhere else. The console speaks
// the one-verb-one-direction pair instead (capability_verbs.go), which is idempotent and needs no
// read-modify-write, so two admins acting at once cannot silently undo each other.
//
//   GET  /edge-deployments/{edge_id}/capabilities                 → 200 { capabilities: [verb] }
//   POST /edge-deployments/{edge_id}/capabilities/{verb}/grant    → 204
//   POST /edge-deployments/{edge_id}/capabilities/{verb}/withdraw → 204
//
// Both writes sit behind `edge.manage`, which is step-up gated, so both go through withStepUp like
// every other act on this surface.
//
// NO TIMER. Delegated authority changes when a person changes it, and nothing on the plane moves it
// on its own — so a poll here would ask a question whose answer only this console's own acts can
// change. Each mutation invalidates the read instead.

/**
 * Composed from the central `edgeDeployments.all` prefix so the existing invalidations reach it,
 * for the same reason `doorsKey` is: it belongs in lib/query-keys.ts beside its siblings and moving
 * it there stays a rename rather than a behaviour change.
 */
function capabilitiesKey(tenantID: string, edgeID: string) {
  return [...queryKeys.edgeDeployments.all(tenantID), "capabilities", edgeID] as const;
}

export function useEdgeAccessOptionsQuery(tenantID: string, edgeID: string | undefined) {
  return useQuery<EdgeAccessOption[], ApiError>({
    queryKey: [...queryKeys.edgeDeployments.all(tenantID), "access-options", edgeID ?? ""],
    queryFn: async () => edgeAccessOptionsResponseSchema.parse(await apiFetch<unknown>(
      `${base(tenantID)}/${encodeURIComponent(edgeID ?? "")}/access-options`,
    )).options,
    enabled: tenantID.length > 0 && !!edgeID,
  });
}

export function useEdgeAccessOptionMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { edgeID: string; option: EdgeAccessOption["id"]; enabled: boolean }>({
    mutationFn: ({ edgeID, option, enabled }) => withStepUp(() => apiFetch<void>(
      `${base(tenantID)}/${encodeURIComponent(edgeID)}/access-options`,
      { method: "POST", body: { option, enabled } },
    )),
    // A lost response can follow a committed decision. Reconcile either outcome.
    onSettled: () => qc.invalidateQueries({ queryKey: queryKeys.edgeDeployments.all(tenantID) }),
  });
}

async function fetchEdgeCapabilities(
  tenantID: string,
  edgeID: string,
): Promise<EdgeCapabilitiesResponse> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/${encodeURIComponent(edgeID)}/capabilities`,
  );
  return edgeCapabilitiesResponseSchema.parse(raw);
}

/**
 * What this tenant has delegated to ONE edge, and what became of the public name that delegation
 * publishes. The full vocabulary is transcribed in schemas.ts.
 *
 * THE NAME'S STATE IS READ WITH THE SET, not beside it, because they can disagree: `relay_serve`
 * can be held while the DNS write that backs it failed, and a console that showed only the set
 * would report an edge as reachable from outside when nothing resolves to it.
 */
export function useEdgeCapabilitiesQuery(tenantID: string, edgeID: string | undefined) {
  return useQuery<EdgeCapabilitiesResponse, ApiError>({
    queryKey: capabilitiesKey(tenantID, edgeID ?? ""),
    queryFn: () => fetchEdgeCapabilities(tenantID, edgeID ?? ""),
    enabled: tenantID.length > 0 && !!edgeID,
  });
}

/**
 * Delegate one verb, or take one back.
 *
 * ONE MUTATION FOR BOTH DIRECTIONS, because they are the same act with the sign flipped and two
 * copies of the invalidation would drift. The direction is required rather than defaulted: a caller
 * that forgot it would either widen or narrow authority silently, and neither is a safe guess.
 *
 * The 400 this can answer is not a generic failure — it names the rule that was broken, in a
 * sentence written for the operator, and the handler sends it in the house shape so the api client
 * resolves the sentence rather than  `capabilityRefusalDetail` strips only the Go package
 * prefix in front of it; the caller shows the rest verbatim.
 */
export function useEdgeCapabilityMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { edgeID: string; verb: string; grant: boolean }>({
    mutationFn: ({ edgeID, verb, grant }) =>
      withStepUp(async () => {
        await apiFetch<void>(
          `${base(tenantID)}/${encodeURIComponent(edgeID)}/capabilities/${encodeURIComponent(verb)}/${
            grant ? "grant" : "withdraw"
          }`,
          { method: "POST" },
        );
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: queryKeys.edgeDeployments.all(tenantID) }),
  });
}

// ── The re-issuance posture ───────────────────────────────────────────────
//
//   PUT  /edge-deployments/{id}/reissuance-policy { requires_admin } → attestationState
//   POST /edge-deployments/{id}/reissuance-window { minutes }        → attestationState
//
// Both sit with the binding's write group (edge.manage + step-up + interactive session): whether a
// machine may take this edge's identity AGAIN without a person is the same class of decision as
// whether a class may take it at all. The server answers with the whole attestation state, which
// replaces the cached one.

export function useEdgeReissuancePolicyMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<AttestationState, ApiError, { edgeID: string; requiresAdmin: boolean }>({
    mutationFn: ({ edgeID, requiresAdmin }) =>
      withStepUp(async () => {
        const raw = await apiFetch<unknown>(
          `${base(tenantID)}/${encodeURIComponent(edgeID)}/reissuance-policy`,
          { method: "PUT", body: { requires_admin: requiresAdmin } },
        );
        return attestationStateSchema.parse(raw);
      }),
    onSuccess: (state, { edgeID }) => {
      qc.setQueryData(attestationKey(tenantID, edgeID), state);
    },
  });
}

export function useEdgeReissuanceWindowMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<AttestationState, ApiError, { edgeID: string; minutes: number }>({
    mutationFn: ({ edgeID, minutes }) =>
      withStepUp(async () => {
        const raw = await apiFetch<unknown>(
          `${base(tenantID)}/${encodeURIComponent(edgeID)}/reissuance-window`,
          { method: "POST", body: { minutes } },
        );
        return attestationStateSchema.parse(raw);
      }),
    onSuccess: (state, { edgeID }) => {
      qc.setQueryData(attestationKey(tenantID, edgeID), state);
    },
  });
}

// ── Containment: the tenant's own stop button, and the way back ────────────
//
//   POST /edge-deployments/{edge_id}/revoke      { reason } → 204
//   POST /edge-deployments/{edge_id}/restore     { reason } → 204
//   GET  /edge-deployments/{edge_id}/containment           → 200 { events: [...] }
//
// Both writes sit behind `edge.manage` (step-up) AND `RequireInteractiveAdmin`, so both go through
// withStepUp like every other act on this surface. The interactive gate is the reason a bearer
// session cannot reach them at all: the same reasoning that refuses an API key the right to approve
// an enrolment refuses it the right to end one, in both directions.
//
// THE READ SITS BEHIND `edge.read` WITH NO STEP-UP, and that difference is load-bearing for the
// polling query below. It shipped in the step-up group with the two acts, which made the record
// unreadable outside a five-minute window and turned every 60s poll into a step_up_demanded audit
// event. Reading why an edge is dead is not the act of killing it.

function containmentKey(tenantID: string, edgeID: string) {
  return [...queryKeys.edgeDeployments.all(tenantID), "containment", edgeID] as const;
}

async function fetchContainmentHistory(
  tenantID: string,
  edgeID: string,
): Promise<ContainmentEvent[]> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/${encodeURIComponent(edgeID)}/containment`,
  );
  return containmentHistoryResponseSchema.parse(raw).events;
}

/**
 * One edge's containment record.
 *
 * 60s, the same as the roster, and NOT because the rows move — they are append-only and immutable
 * once written. It is because a NEW row can arrive from somewhere this console cannot see: PureID
 * can still contain an edge from the provider's host, and an incident is exactly when a second
 * administrator is acting on the same machine. The roster's badge would flip on its own timer while
 * a frozen record beside it still said the edge had never been touched.
 */
export function useContainmentHistoryQuery(tenantID: string, edgeID: string | undefined) {
  return useQuery<ContainmentEvent[], ApiError>({
    queryKey: containmentKey(tenantID, edgeID ?? ""),
    queryFn: () => fetchContainmentHistory(tenantID, edgeID ?? ""),
    enabled: tenantID.length > 0 && !!edgeID,
    ...LIVE_READ_OPTIONS,
  });
}

/**
 * Cut an edge off, or give it its authority back.
 *
 * ONE MUTATION FOR BOTH DIRECTIONS, and the direction is required rather than defaulted, for the
 * same reason the capability pair is: a caller that forgot it would either contain a machine or
 * un-contain one silently, and neither is a safe guess.
 *
 * THE REASON IS SENT TRIMMED AND THE SERVER STILL CHECKS IT. Both directions refuse an empty one
 * with a 400 carrying a sentence; the forms in front of this refuse first so the passkey is not
 * spent on a request that cannot succeed.
 *
 * The roster is invalidated as well as the record: `revoked` and `enforcing` both move, and
 * revoking clears `status` back to pending in the same statement, so the row on the page behind
 * this dialog is wrong the moment the call returns.
 */
export function useEdgeContainmentMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { edgeID: string; revoke: boolean; reason: string }>({
    mutationFn: ({ edgeID, revoke, reason }) =>
      withStepUp(async () => {
        await apiFetch<void>(
          `${base(tenantID)}/${encodeURIComponent(edgeID)}/${revoke ? "revoke" : "restore"}`,
          { method: "POST", body: { reason } },
        );
      }),
    onSuccess: (_data, { edgeID }) => {
      void qc.invalidateQueries({ queryKey: containmentKey(tenantID, edgeID) });
      void qc.invalidateQueries({ queryKey: queryKeys.edgeDeployments.list(tenantID) });
    },
  });
}

/**
 * Contain an edge for a stolen key (contract I13): revoke, plus the key denylist, the sessions and
 * the public certificates, and never lifted. The same gate as revoke. It answers what it did.
 */
export function useEdgeContainMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<EdgeContainmentReceipt, ApiError, { edgeID: string; reason: string }>({
    mutationFn: ({ edgeID, reason }) =>
      withStepUp(async () =>
        edgeContainmentReceiptSchema.parse(
          await apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(edgeID)}/contain`, { method: "POST", body: { reason } }),
        ),
      ),
    onSuccess: (_receipt, { edgeID }) => {
      void qc.invalidateQueries({ queryKey: containmentKey(tenantID, edgeID) });
      void qc.invalidateQueries({ queryKey: queryKeys.edgeDeployments.list(tenantID) });
      void qc.invalidateQueries({ queryKey: servingCertificatesKey(tenantID, edgeID) });
    },
  });
}

// ── Public serving certificates (contract I12) ────────────────────────────
//
//   GET  /edge-deployments/{edge_id}/serving-certificates                    (edge.read)
//   POST /edge-deployments/{edge_id}/serving-certificates/{order_id}/revoke  (edge.manage + step-up + interactive)

function servingCertificatesKey(tenantID: string, edgeID: string) {
  return [...queryKeys.edgeDeployments.all(tenantID), "serving-certificates", edgeID] as const;
}

/** The public certificates an edge holds for its private applications, newest first, with what the
 *  public record of issued certificates says about the tenant's names when this deployment reads one;
 *  60s like the roster. */
export function useServingCertificatesQuery(tenantID: string, edgeID: string | undefined, enabled = true) {
  return useQuery<ServingCertificateList, ApiError>({
    queryKey: servingCertificatesKey(tenantID, edgeID ?? ""),
    queryFn: async () =>
      servingCertificateListSchema.parse(
        await apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(edgeID ?? "")}/serving-certificates`),
      ),
    enabled: enabled && tenantID.length > 0 && !!edgeID,
    ...LIVE_READ_OPTIONS,
  });
}

/** Revoke one certificate; the edge stops serving it at its next pull and orders a replacement. */
export function useRevokeServingCertificateMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<ServingCertificateRevocation, ApiError, { edgeID: string; orderID: string; reason: RevocationReason }>({
    mutationFn: ({ edgeID, orderID, reason }) =>
      withStepUp(async () =>
        servingCertificateRevocationSchema.parse(
          await apiFetch<unknown>(
            `${base(tenantID)}/${encodeURIComponent(edgeID)}/serving-certificates/${encodeURIComponent(orderID)}/revoke`,
            { method: "POST", body: { reason } },
          ),
        ),
      ),
    onSuccess: (_rev, { edgeID }) => {
      void qc.invalidateQueries({ queryKey: servingCertificatesKey(tenantID, edgeID) });
    },
  });
}

// ── The attestation binding ───────────────────────────────────────────────
//
//   GET    /edge-deployments/{id}/attestation → attestationState   (edge.read, no step-up)
//   PUT    /edge-deployments/{id}/attestation → attestationState   (edge.manage + step-up +
//                                                                   interactive session)
//   DELETE /edge-deployments/{id}/attestation → attestationState   (edge.manage + step-up)
//
// THE READ IS NOT BEHIND STEP-UP AND THE WRITES ARE, which is the same split the rest of this
// surface lives by. "Which workload class can become this edge without a person" is a question an
// operator asks during an incident, beside the instance keys; behind a passkey it would be
// unanswerable outside a five-minute window.
//
// NO TIMER. Nothing but an act on this page moves the binding, so a poll would ask a question only
// this console can change the answer to — the same reasoning as the capabilities read above.

function attestationKey(tenantID: string, edgeID: string) {
  return [...queryKeys.edgeDeployments.all(tenantID), "attestation", edgeID] as const;
}

async function fetchAttestation(tenantID: string, edgeID: string): Promise<AttestationState> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/${encodeURIComponent(edgeID)}/attestation`,
  );
  return attestationStateSchema.parse(raw);
}

export function useEdgeAttestationQuery(tenantID: string, edgeID: string | undefined) {
  return useQuery<AttestationState, ApiError>({
    queryKey: attestationKey(tenantID, edgeID ?? ""),
    queryFn: () => fetchAttestation(tenantID, edgeID ?? ""),
    enabled: tenantID.length > 0 && !!edgeID,
  });
}

/**
 * Pin a binding, or withdraw one.
 *
 * ONE MUTATION, TWO DIRECTIONS, and the direction is required rather than defaulted — a caller that
 * forgot it would either grant or withdraw a standing machine grant silently.
 *
 * THE PEM NEVER ENTERS A CACHE, and neither does the response's echo of it: the server answers in
 * fingerprints, and the form's plaintext lives in the mutation's argument and the form state only.
 * `setQueryData` writes the server's answer, which carries no material.
 */
export function useEdgeAttestationMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<
    AttestationState,
    ApiError,
    { edgeID: string; binding: PinAttestationForm | null }
  >({
    mutationFn: ({ edgeID, binding }) =>
      withStepUp(async () => {
        const url = `${base(tenantID)}/${encodeURIComponent(edgeID)}/attestation`;
        const raw =
          binding === null
            ? await apiFetch<unknown>(url, { method: "DELETE" })
            : await apiFetch<unknown>(url, {
                method: "PUT",
                body: {
                  method: binding.method,
                  issuer: binding.issuer,
                  audience: binding.audience,
                  subject: binding.subject,
                  signing_keys_pem: splitPEMBlocks(binding.signing_keys_pem),
                  jwks_url: binding.jwks_url,
                  jwks_tls_trust_pem: binding.jwks_tls_trust_pem,
                },
              });
        return attestationStateSchema.parse(raw);
      }),
    onSuccess: (state, { edgeID }) => {
      qc.setQueryData(attestationKey(tenantID, edgeID), state);
      // The binding decides who can recover a certificate AND whose evidence key can be trusted
      // without a person, so a change here can change what the instance-keys panel will show next.
      void qc.invalidateQueries({
        queryKey: queryKeys.edgeDeployments.evidenceKeys(tenantID, edgeID),
      });
    },
  });
}

// ── Who may reach the tenant's private applications ────────────────────────
//
// One tenant-wide setting, read on edge.read and changed with step-up. No timer: only an
// administrator's act changes it, and the mutation writes the answer back.

function relayAdmissionKey(tenantID: string) {
  return [...queryKeys.edgeDeployments.all(tenantID), "relayAdmission"] as const;
}

export function useRelayAdmissionQuery(tenantID: string, enabled: boolean) {
  return useQuery<RelayAdmissionPolicy, ApiError>({
    queryKey: relayAdmissionKey(tenantID),
    queryFn: async () => relayAdmissionPolicySchema.parse(await apiFetch<unknown>(`${base(tenantID)}/relay-admission`)),
    enabled: enabled && tenantID.length > 0,
  });
}

export function useRelayAdmissionMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<RelayAdmissionPolicy, ApiError, { mode: RelayAdmissionMode; declared_egress_ranges: string[] }>({
    mutationFn: (body) =>
      withStepUp(async () =>
        relayAdmissionPolicySchema.parse(
          await apiFetch<unknown>(`${base(tenantID)}/relay-admission`, { method: "PUT", body }),
        ),
      ),
    onSuccess: (policy) => qc.setQueryData(relayAdmissionKey(tenantID), policy),
  });
}

/**
 * AUTHORISE AN EDGE THAT DOES NOT EXIST YET — POST /edge-deployments/authorise.
 *
 * One act, behind step-up and the interactive gate, and the last time a person is asked anything.
 * The administrator states the name, the doors, the operator and the workload class; a machine that
 * matches enrols itself later, unattended, with the identity its own cluster issued.
 *
 * It creates a REGISTRY ROW, so the roster is what changes — invalidate the list, not a detail. The
 * new edge's own attestation cache is untouched because it did not exist to be cached.
 *
 * The PEM never enters a cache. It travels in the mutation's argument and lives in form state; the
 * server answers with an identity and a sentence, carrying no material.
 */
export function useAuthoriseEdgeMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<AuthoriseEdgeResponse, ApiError, AuthoriseEdgeForm>({
    mutationFn: (values) =>
      withStepUp(async () => {
        const raw = await apiFetch<unknown>(`${base(tenantID)}/authorise`, {
          method: "POST",
          body: {
            edge_id: values.edge_id,
            role: values.role,
            operator: values.operator,
            deployment_id: values.deployment_id,
            deployment_edge_ids: values.deployment_edge_ids,
            ...(admitsPeople(values.role) ? {} : { attestation: {
              method: values.method,
              issuer: values.issuer,
              audience: values.audience,
              subject: values.subject,
              signing_keys_pem: splitPEMBlocks(values.signing_keys_pem),
              jwks_url: values.jwks_url,
              jwks_tls_trust_pem: values.jwks_tls_trust_pem,
            }}),
          },
        });
        return authoriseEdgeResponseSchema.parse(raw);
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.edgeDeployments.list(tenantID) });
    },
  });
}

export function useEdgeRegistrationsQuery(tenantID: string) {
  return useQuery({
    queryKey: [...queryKeys.edgeDeployments.all(tenantID), "registrations"],
    queryFn: async () => edgeRegistrationsSchema.parse(await apiFetch<unknown>(`${base(tenantID)}/registrations`)).registrations,
    refetchInterval: (query) => query.state.data?.some((item) => item.state === "pending") ? 10_000 : false,
  });
}
// SITES. Read on the same key family as the rest of the deployments surface, so creating one
// refreshes the picker the prepare dialog reads without a second invalidation rule.
export function useEdgeSitesQuery(tenantID: string) {
  return useQuery({
    queryKey: [...queryKeys.edgeDeployments.all(tenantID), "sites"],
    queryFn: async () => edgeSitesSchema.parse(await apiFetch<unknown>(`${base(tenantID)}/sites`)).sites,
  });
}

// Creating a site PINS A CLUSTER: anything in it satisfying an approved class can become this
// tenant's runtime, and no other tenant may pin it while the site lives. Step-up for that reason —
// it is the same class of act as pinning a workload class, not a setting.
export function useCreateEdgeSiteMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<EdgeSite, ApiError, CreateEdgeSiteForm>({
    mutationFn: (body) => withStepUp(async () => edgeSiteSchema.parse(await apiFetch<unknown>(`${base(tenantID)}/sites`, { method: "POST", body }))),
    // Refresh the picker before the caller reopens Add Edge. Opening it against
    // the old empty list can leave the newly mounted select without a value.
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.edgeDeployments.all(tenantID) }),
  });
}

export function usePrepareEdgeMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<EdgeRegistration, ApiError, PrepareEdgeForm>({
    mutationFn: (body) => withStepUp(async () => edgeRegistrationSchema.parse(await apiFetch<unknown>(`${base(tenantID)}/registrations`, { method: "POST", body }))),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: queryKeys.edgeDeployments.all(tenantID) }); },
  });
}
/** Preparing a changed release grants no authority; its new code still requires a signature. */
export function usePrepareEdgeImageUpdateMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<EdgeRegistration, ApiError, { edge_id: string; expected_approval_id: string; lifecycle_operation: "software_update" | "certificate_recovery" }>({
    mutationFn: ({ edge_id, expected_approval_id, lifecycle_operation }) => withStepUp(async () =>
      edgeRegistrationSchema.parse(await apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(edge_id)}/registration-updates`, {
        method: "POST", body: { expected_approval_id, lifecycle_operation },
      }))),
    retry: false,
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.edgeDeployments.all(tenantID) }),
  });
}
export function useDecideEdgeRegistrationMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, EdgeRegistrationDecision>({
    mutationFn: ({ intent_id, ...body }) => withStepUp(() => apiFetch<void>(`${base(tenantID)}/registrations/${encodeURIComponent(intent_id)}/decide`, { method: "POST", body })),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: queryKeys.edgeDeployments.all(tenantID) }); },
  });
}

/** Review requires an administrator session; final approval performs action-bound verification. */
export function useLookupEdgeRegistrationMutation(tenantID: string) {
  return useMutation<EdgeRegistrationProposal, ApiError, { intent_id: string; enrollment_code: string }>({
    mutationFn: ({ intent_id, enrollment_code }) => withStepUp(async () => edgeRegistrationProposalSchema.parse(
      await apiFetch<unknown>(`${base(tenantID)}/registrations/${encodeURIComponent(intent_id)}/lookup`, {
        method: "POST", body: { enrollment_code },
      }),
    )),
    retry: false,
    gcTime: 0,
  });
}

export type PrepareRegistrationApproval = {
  intent_id: string; proposal_id: string; digest: string; enrollment_code: string;
  max_live_instances: number; unattended_growth: "allowed" | "per_instance_approval";
  image_update?: boolean;
  combined?: boolean;
  platform_override_reason?: string;
};

export function usePrepareRegistrationApprovalMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<EdgeManifestApproval, ApiError, PrepareRegistrationApproval>({
    mutationFn: ({ intent_id, image_update, combined, ...body }) => withStepUp(async () => {
      const { max_live_instances: _maximum, unattended_growth: _growth, ...updateBody } = body;
      const raw = await apiFetch<unknown>(`${base(tenantID)}/registrations/${encodeURIComponent(intent_id)}/${combined ? "combined-approval" : image_update ? "image-update-approval" : "approval"}`, {
        method: "POST", body: image_update && !combined ? updateBody : body,
      });
      return (combined ? edgeCombinedApprovalSchema : image_update ? edgeImageUpdateApprovalSchema : edgeApprovalSchema).parse(raw);
    }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: queryKeys.edgeDeployments.all(tenantID) }); },
  });
}

export function useReadEdgeApprovalMutation(tenantID: string) {
  return useMutation<EdgeManifestApproval, ApiError, { id: string; image_update: boolean; combined?: boolean }>({
    mutationFn: ({ id, image_update, combined }) => withStepUp(async () => (combined ? edgeCombinedApprovalSchema : image_update ? edgeImageUpdateApprovalSchema : edgeApprovalSchema).parse(
      await apiFetch<unknown>(`${base(tenantID)}/${combined ? "combined-approvals" : image_update ? "registration-updates/approvals" : "approvals"}/${encodeURIComponent(id)}`),
    )),
    gcTime: 0,
  });
}

export function useSignEdgeApprovalMutation(tenantID: string) {
  const qc = useQueryClient();
  const [, dispatch] = useReducer(ceremonyReducer, { status: "idle" });
  return useMutation<EdgeManifestApproval, Error, EdgeManifestApproval>({
    mutationFn: (approval) => withStepUp(async () => {
      const id = approval.id;
      let ceremonyID = "";
      const imageUpdate = approval.manifest.format === "edge_manifest-v3";
      const combined = approval.manifest.format === "edge_manifest-v4";
      const responseSchema = combined ? edgeCombinedApprovalSchema : imageUpdate ? edgeImageUpdateApprovalSchema : edgeApprovalSchema;
      const url = `${base(tenantID)}/${combined ? "combined-approvals" : imageUpdate ? "registration-updates/approvals" : "approvals"}/${encodeURIComponent(id)}/sign`;
      return runAssertion(dispatch, async () => {
        const response = edgeApprovalBeginSchema.parse(await apiFetch<unknown>(`${url}/begin`, { method: "POST" }));
        const challenge = base64urlDecode(response.options.challenge);
        const prefix = new TextEncoder().encode("pureid.edge-approval.v1\0");
        const hex = (bytes: Uint8Array) => Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
        if (challenge.length !== prefix.length + 80 ||
            !prefix.every((byte, index) => challenge[index] === byte) ||
            hex(challenge.slice(prefix.length, prefix.length + 32)) !== approval.manifest_hash ||
            hex(challenge.slice(prefix.length + 32, prefix.length + 48)) !== approval.id.replaceAll("-", "")) {
          throw new Error("The signing request differs from the installation you reviewed. Reload the approval.");
        }
        ceremonyID = response.ceremony_id;
        return {
          ...response.options,
          allowCredentials: response.options.allowCredentials?.map((credential) => ({
            ...credential,
            transports: credential.transports?.filter((transport) => transport !== "smart-card"),
          })),
        };
      }, async (credential) => responseSchema.parse(await apiFetch<unknown>(`${url}/finish`, {
        method: "POST", body: { ceremony_id: ceremonyID, credential },
      })));
    }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: queryKeys.edgeDeployments.all(tenantID) }); },
    gcTime: 0,
  });
}
